<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import IconClose from '@lahaus-roomie/roomie/src/assets/icons/close.svg'
import InstructionsIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/instructions.svg'
import TourIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/tour.svg'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'

import { SCREEN } from '@/utils/segment'
import { LazyAppModal } from '#components'
import { useUserStore } from '@/stores/userStore'
import useListingsSearch from '@/composables/useListingsSearch'
import useSearchResultsMetadata from '@/composables/useSearchResultsMetadata'
import type { InvestmentProjectSummary } from '@/server/api/buyer-front/types'
import OnboardingFiltersBar from '@/components/OnboardingContent/FiltersBar/index.vue'
import SearchResultsContentHeader from '@/components/SearchResultsContent/Header/index.vue'
import OnboardingEmptyResultsBanner from '@/components/OnboardingContent/EmptyResultsBanner/index.vue'
import SearchResultsContentHero from '@/components/SearchResultsContent/Hero/index.vue'
import OnboardingResultsCarousels from '@/components/OnboardingContent/ResultsCarousels/index.vue'
import OnboardingStepsLoader from '@/components/BrandedV2/OnboardingSteps/Loader/index.vue'
import SearchResultMap from '@/components/BrandedV2/SearchResultMap/index.vue'
import UserIdentityContainer from '@/components/App/UserIdentityContainer/index.vue'
import { parseMetadata } from '@/composables/useSearchResultsMetadata/utils'
import { useSearchFiltersStore } from '@/stores/searchFiltersStore'

import { useThemeStore } from '@/stores/useThemeStore'
import './_index.scss'
import { delay } from '@/utils/delay'

const themeStore = useThemeStore()
const { $useLhId } = useNuxtApp()
const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const showMap = ref(false)
const showResultsProjects = ref(true)
const isRegistrationModalOpen = ref(false)
const titleButton = ref<string>(t('show_map_button'))
const SRPMapElement = ref<HTMLElement | null>(null)

const toggleShowMap = async () => {
  if (isContentLocked.value) {
    isRegistrationModalOpen.value = true
    return
  }
  const className = 'overflow-hidden'
  showMap.value = !showMap.value
  showResultsProjects.value = !showMap.value
  titleButton.value = t('show_map_button')
  document.body.classList.remove(className)

  if (showMap.value) {
    titleButton.value = t('show_project_list')
    await delay(100)
    scrollToNav()
    document.body.classList.add(className)
  }
}

const showMapByRoute = async () => {
  if (route.query['view-map'] === 'true' && !isContentLocked.value) {
    await toggleShowMap()
  }
}

const ButtonMapIcon = computed(() => {
  return showMap.value ? InstructionsIcon : TourIcon
})

const url = useRequestURL()

const { getMetadata } = useSearchResultsMetadata()

const searchFiltersStore = useSearchFiltersStore()

await callOnce(async () => {
  await searchFiltersStore.initialize()
})

const { data: metadataData } = await useAsyncData<SeoMetadata>(`seometadata/${route.path}`, async () => {
  return await getMetadata()
})

const { baseMetadata = {}, customSrpOptions = {} } = metadataData.value || {}

const { search, isLoading, results, emitListingsSearchedEvent } = useListingsSearch({
  screen: SCREEN.INVESTMENT_SIMULATOR,
  ...customSrpOptions
})

const { data: initialResults } = await useAsyncData<SearchResults>('listings-search', async () => {
  return await search()
})

results.value = initialResults.value

const totalResultsCount = computed(() => {
  if (!results.value) return 0

  const exactMatchResultsCount = results.value.exactMatchProjects.length
  const extendedSearchResultsCount = results.value.extendedSearch.map(result => result.projects).flat().length
  const extraResultsCount = results.value.extraResults.map(result => result.projects).flat().length

  return exactMatchResultsCount + extendedSearchResultsCount + extraResultsCount
})

const metadata = computed(() => {
  return parseMetadata(
    {
      metadata: baseMetadata || {},
      resultsCount: totalResultsCount.value
    },
    { t, locale: locale.value }
  )
})

useSeoMeta(metadata.value)

const generateRealEstateListingSchema = () => {
  const locationName = t('country')

  const itemListElements = results.value?.exactMatchProjects?.map((project, index) => {
    const propertyUrl = `${url.origin}${project.path}`
    const roomCount = project.typologies && project.typologies.length > 0 ? project.typologies[0].bedroom : undefined
    const bathroomCount = project.typologies && project.typologies.length > 0 ? project.typologies[0].bathroom : undefined
    return {
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@context': 'https://schema.org',
        '@type': 'RealEstateListing',
        name: project.name,
        description: project.description,
        url: propertyUrl,
        image: project.cover,
        offers: {
          '@type': 'Offer',
          priceCurrency: project.currency,
          price: project.price,
          businessFunction: 'http://purl.org/goodrelations/v1#sell',
          itemOffered: {
            '@type': 'Accommodation',
            name: project.name,
            description: project.description,
            numberOfRooms: roomCount,
            numberOfBathroomsTotal: bathroomCount,
            address: {
              '@type': 'PostalAddress',
              addressLocality: project.city_label,
              addressRegion: project.zone_label,
              addressCountry: locale.value.toUpperCase(),
              streetAddress: project.neighborhood_label
            },
            geo: project.latitude && project.longitude
              ? {
                '@type': 'GeoCoordinates',
                latitude: project.latitude,
                longitude: project.longitude
              }
              : undefined,
            yearBuilt: project.delivered_at
          }
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': propertyUrl
        },
        datePosted: new Date().toISOString()
      }
    }
  })

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Listado de Proyectos de Vivienda Nuevos en ${locationName}`,
    numberOfItems: itemListElements?.length ?? 0,
    itemListElement: itemListElements
  }
}

const generatedSchema = generateRealEstateListingSchema()

useHead({
  link: [{ rel: 'canonical', href: metadata.value?.canonicalUrl || route.path }],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify(generatedSchema)
    }
  ]
})

const { y } = useWindowScroll()

const outstandingProject = computed(() => results.value?.outstandingProject?.code ? results.value.outstandingProject : null)

const isHeroVisible = computed(() => !!outstandingProject.value?.code)

const canShowFloatingFiltersBar = computed(() => y.value > 170)

const isContentLocked = computed(() => !userStore.isAuthenticated)

const isRegistrationBannerOpen = ref(true)

const whatsappUrl = computed(() => {
  const location = outstandingProject.value?.business_hub || t('country')
  const message = t('whatsapp_message', { location })
  return `https://wa.me/525514024356?text=${encodeURIComponent(message)}`
})

const showMapButton = computed<boolean>(() => {
  return (results.value && outstandingProject.value)
})

const registrationBannerClicked = () => {
  const onboardingHero = document.querySelector('.onboarding-v2__hero')
  onboardingHero?.dispatchEvent(new MouseEvent('click', { bubbles: true }))
}

const scrollToNav = () => {
  const navElement: HTMLElement | null = document.querySelector('nav')
  if (navElement) {
    navElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

const onRegistrationSubmit = async () => {
  await router.replace({
    path: route.path,
    query: {
      ...route.query,
      'view-map': 'true'
    }
  })

  isRegistrationModalOpen.value = false
  await toggleShowMap()
}

onMounted(() => {
  emitListingsSearchedEvent({ userAction: 'DEFAULT_FILTERS' })

  const appHeader = document.querySelector('.app-header')
  if (appHeader) {
    appHeader.setAttribute('data-position', 'absolute')
  }

  showMapByRoute()
})

onUnmounted(() => {
  const appHeader = document.querySelector('.app-header')
  if (appHeader) {
    appHeader.removeAttribute('data-position')
  }
})
</script>

<script lang="ts">
export default {
  name: 'SearchResultsContent'
}
</script>

<template>
  <div
    class="search-results-page"
    :class="{
      'mt-60 md:mt-28': themeStore.isV2
    }">
    <OnboardingStepsLoader
      v-if="isLoading && themeStore.isV2" />

    <div
      v-show="canShowFloatingFiltersBar"
      :class="{
        'bg-pine-600': themeStore.isV2,
        'bg-white': !themeStore.isV2
      }"
      class="fixed w-full left-0 top-0 z-50">
      <OnboardingFiltersBar
        :placeholder="t('emptyFiltersPlaceholder')"
        :screen="SCREEN.SRP"
        :class="{
          'py-16 md:py-8': themeStore.isV2,
          'py-8': !themeStore.isV2
        }"
        class="max-w-[1800px] md:px-40 mx-auto md:mb-16 mt-0 md:mt-12 pr-24 md:pr-36" />
    </div>

    <Transition name="slide-down">
      <SearchResultsContentHeader
        ref="SRPMapElement"
        :title="metadata.titleH1"
        :screen="SCREEN.SRP"
        :enable-title="!!outstandingProject" />
    </Transition>

    <div
      v-if="isContentLocked && isRegistrationBannerOpen && isHeroVisible && themeStore.isV2"
      class="flex justify-center w-full">
      <div class="flex-1 px-24 md:px-40 max-w-[1800px]">
        <div class="mt-24 md:mt-40 -mb-24 md:-mb-16 px-24 py-16 bg-pine-200 rounded-lg relative flex items-center justify-between">
          <span
            v-lh-id="{P: SCREEN.SRP, Z: 'resultados de la busqueda', A: 'enviar', C: 'registrar' }"
            class="link-1-sb cursor-pointer"
            @click="registrationBannerClicked">
            {{ t('registrationBanner', { count: totalResultsCount }) }}
          </span>

          <span
            id="registration-banner-close"
            v-lh-id="{P: SCREEN.SRP, Z: 'resultados de la busqueda', A: 'enviar', C: 'registrar' }"
            class="cursor-pointer"
            :data-lh-id="`${SCREEN.SEARCH_RESULTS}-registration-banner-close`"
            @click="isRegistrationBannerOpen = false">
            <IconClose class="w-24 h-24" />
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="themeStore.isV2 && showResultsProjects"
      class="bg-white px-24 max-w-[1800px] md:px-40 pb-24 md:pb-32 pt-48 md:pt-56 mx-auto">
      <div class="w-full flex flex-col lg:flex-row lg:justify-between lg:items-center">
        <div class="w-full">
          <h1
            v-if="metadata.titleH1"
            class="search-results-header__title--custom display-7 md:display-6 text-pine-600">
            {{ metadata.titleH1 }}
          </h1>

          <h1
            v-else
            class="search-results-header__title--default display-7 md:display-6 text-pine-600">
            {{ t('title', { count: searchFiltersStore.filterCount }) }}
          </h1>

          <p
            class="body-1-m text-pine-600 mt-4">
            {{ t('disclaimer') }}
          </p>
        </div>

        <div
          v-if="showMapButton && showResultsProjects && locale === 'mx'"
          class="mt-lh-24 lg:mt-0">
          <RoomieButton
            id="view-map-or-list-projects"
            variant="secondary"
            icon-placement="right"
            class="w-max"
            :data-lh-id="$useLhId({P: SCREEN.SRP, Z: 'hero', A: 'abrir', C: 'mapa'})"
            @click="toggleShowMap">
            {{ titleButton }}
            <template #icon>
              <ButtonMapIcon />
            </template>
          </RoomieButton>
        </div>
      </div>
    </div>

    <div
      v-if="showMapButton && showMap && locale === 'mx'"
      class="absolute right-7 md:right-16 z-10 mt-lh-40">
      <RoomieButton
        id="view-map-or-list-projects"
        variant="secondary"
        icon-placement="right"
        class="w-max"
        :data-lh-id="$useLhId({P: SCREEN.SRP, Z: 'hero', A: 'cerrar', C: 'mapa'})"
        @click="toggleShowMap">
        {{ titleButton }}
        <template #icon>
          <ButtonMapIcon />
        </template>
      </RoomieButton>
    </div>

    <Transition name="slide-down">
      <div
        v-if="isHeroVisible && showResultsProjects"
        class="max-w-[1800px] mx-auto px-0 md:px-40"
        :class="{ 'mb-40': !isContentLocked, 'mb-32': isContentLocked }">
        <SearchResultsContentHero
          :screen="SCREEN.SRP"
          class="onboarding-v2__hero"
          :class="{ 'blur-sm': isLoading }"
          :listing="outstandingProject as InvestmentProjectSummary"
          :hide-title="isContentLocked" />
      </div>
    </Transition>

    <OnboardingEmptyResultsBanner
      v-if="!outstandingProject"
      :screen="SCREEN.SRP"
      class="search-results-page__empty-results-banner max-w-[1800px] mx-auto" />

    <Transition name="slide-down">
      <OnboardingResultsCarousels
        v-if="results && showResultsProjects"
        class="max-w-[1800px] mx-auto"
        :class="{ 'blur-sm': isLoading, 'mb-40': !isContentLocked, 'pt-24 mb-0': isContentLocked }"
        :exact-match-projects="results.exactMatchProjects"
        :extended-search="results.extendedSearch"
        :extra-results="results.extraResults"
        :screen="SCREEN.SRP"
        :is-content-locked="isContentLocked"
        :is-loading />
    </Transition>

    <Transition name="slide-down">
      <div
        v-if="results && showMap && outstandingProject"
        class="w-full h-[calc(100vh_-_108px_-_80px)] lg:h-[calc(100vh_-_72px_-_80px)]">
        <SearchResultMap
          :outstanding-project="outstandingProject"
          :projects="results.exactMatchProjects"
          :data-attributes="{dataLh: { P: SCREEN.SRP, Z: 'resultados de la busqueda en el mapa' }}" />
      </div>
    </Transition>

    <a
      v-if="locale === 'mx'"
      :href="whatsappUrl"
      :data-lh-id="$useLhId({P: SCREEN.SRP, Z: 'flotante', A: 'abrir', C: 'whatsapp'})"
      target="_blank"
      rel="noopener noreferrer"
      class="fixed bottom-24 right-7 md:bottom-32 md:right-16 z-50 flex items-center justify-center w-[66px] h-[66px] p-[10px] rounded-full bg-[#25D366] hover:bg-[#25D366] transition-colors duration-300 shadow-[0px_0px_16.5px_0px_rgba(0,0,0,0.40)]">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="34"
        viewBox="0 0 32 34"
        fill="none"
        class="w-32 h-32 fill-white pointer-events-none">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M23.4206 20.6012C23.0152 20.3793 21.0236 19.3044 20.6499 19.1526C20.2772 19.0008 20.0046 18.9222 19.7131 19.325C19.4221 19.7267 18.5961 20.6276 18.3457 20.8938C17.8702 21.3978 16.9445 20.7346 16.4263 20.4457C15.8296 20.1136 15.0494 19.6023 14.2038 18.7899C13.0177 17.6497 12.2401 16.267 12.0145 15.8452C11.6703 15.2005 12.8764 14.4472 13.247 13.7577C13.267 13.7197 13.2892 13.6791 13.3124 13.637C13.46 13.3665 13.4004 13.123 13.3045 12.9121C13.2091 12.7023 12.4594 10.6396 12.1468 9.80032C11.7494 8.73494 9.99235 8.81981 9.33393 9.48772C8.94014 9.88678 7.83575 10.8451 7.75984 12.8773C7.6834 14.9079 9.08933 16.9264 9.28543 17.209C9.48153 17.4926 11.9692 21.8954 16.1032 23.7062C18.5492 24.7768 20.4996 25.769 22.856 24.1675C23.3205 23.8522 23.7185 23.4663 23.8792 23.0657C24.2593 22.1147 24.2899 21.2881 24.1929 21.1121C24.0959 20.9365 23.8255 20.8216 23.4201 20.6007L23.4206 20.6012Z"
          fill="white" />

        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M27.3368 5.5994C24.3288 2.58881 20.3287 0.929843 16.0656 0.927734C7.28425 0.927734 0.136534 8.07282 0.133371 16.8547C0.131789 19.6624 0.866119 22.4025 2.25992 24.8174L0 33.0711L8.44663 30.8565C10.7735 32.1254 13.3935 32.7944 16.0604 32.7949H16.0672C24.8486 32.7949 31.9963 25.6493 32 16.8668C32.0016 12.6111 30.3458 8.60894 27.3378 5.59834L27.3368 5.5994ZM16.0662 30.1048H16.0609C13.6845 30.1038 11.3534 29.4659 9.32066 28.2598L8.83726 27.9725L3.82505 29.2867L5.16297 24.4015L4.84826 23.9007C3.52299 21.7926 2.8224 19.3566 2.82398 16.8558C2.82661 9.55624 8.76767 3.61782 16.072 3.61782C19.6092 3.6194 22.934 4.99844 25.4343 7.50033C27.9346 10.0022 29.311 13.3286 29.3094 16.8663C29.3068 24.1659 23.3662 30.1048 16.0667 30.1048H16.0662Z"
          fill="white" />
      </svg>
    </a>

    <div
      v-if="showMapButton && locale === 'co'"
      class="w-max fixed z-10 bottom-12 left-1/2 transform -translate-x-1/2">
      <RoomieButton
        id="view-map-or-list-projects"
        variant="primary"
        icon-placement="right"
        :data-lh-id="$useLhId({P: SCREEN.SRP, Z: 'flotante', A: showMap ? 'abrir' : 'cerrar', C: 'mapa'})"
        @click="toggleShowMap">
        {{ titleButton }}
        <template #icon>
          <ButtonMapIcon />
        </template>
      </RoomieButton>
    </div>

    <Teleport to="body">
      <LazyAppModal
        v-if="isRegistrationModalOpen"
        class="lock-overlay__registration-modal !fixed top-0 z-60"
        :close-tracking-lh-id="$useLhId({ P: SCREEN.SRP, Z: 'modal de registro o autenticacion', A: 'cerrar', C: 'cerrar_icon' })"
        @close="isRegistrationModalOpen = false">
        <UserIdentityContainer
          :id="`${SCREEN.SRP}-user-identity-container`"
          :screen="SCREEN.SRP"
          :listing-id="outstandingProject?.code"
          :data-attributes="{ dataLh: {P: SCREEN.SRP, Z: 'resultado de proyectos en el mapa'}}"
          @submit="onRegistrationSubmit" />
      </LazyAppModal>
    </Teleport>
  </div>
</template>

<i18n src="./i18n.json" />

<style lang="scss">
  .roomie-footer {
    &__navigation-title {
      @apply font-bold;
    }
  }
</style>
